import React, { useState } from "react";
import { ICreditResponse, ISummaryItems } from "../../../../store/credit/types";
import { formatCurrency } from "../../../../util/credit";
import { getDate } from "../../../../util/date";
import { getFormattedDate } from "../../../../util/dateFormatting";
import i18nService from "../../../../util/Internationalization/i18n/i18n";

interface IProps {
    data?: ICreditResponse;
    isOneTimeCreditServiceActive?: boolean;
    reportSummaryHeader?: any;
}

const PrintCreditSummary: React.FunctionComponent<IProps> = (props) => {
    let col = null;
    if (props.data != null) {
        if (props.data.creditReport != null && props.data.creditReport.summary != null) {
            col = props.data.creditReport.summary.sort((a, b) => (a.id > b.id ? 1 : -1));
        }
    }
    const [nextPullInYears, setNextPullInYears] = useState(-1);
    let showRefreshScore = false;
    let today = getDate(new Date().setHours(0, 0, 0, 0));
    if (props.data != null) {
        let nextPull = props.data["next-update-date"] == null ? getDate(props.data["update-date"]) : getDate(props.data["next-update-date"]);
        let yearDiff = nextPull.diff(today, "years");

        if (nextPullInYears === -1) {
            setNextPullInYears(yearDiff);
        }

        let diff = today.diff(nextPull, "days");
        if (!isNaN(diff) && nextPull.diff(today, "days") <= 0) {
            showRefreshScore = true;
        } else {
            showRefreshScore = false;
        }
    }

    return (
        <React.Fragment>
            <div className="box offset-padding">
                {col != null ? (
                    <React.Fragment>
                        <div className="box-header">
                            <div className="box-title-container">
                                <div className="box-title-text">
                                    <h2 className="box-title">{props.reportSummaryHeader}</h2>
                                </div>
                            </div>
                            <div className="box-helper">
                                {props.data != null ? (
                                    <div className="row between-xs">
                                        <div className="col-xs col-gutter-vertical flex-grow-null">
                                            {showRefreshScore === false && nextPullInYears < 2 ? (
                                                <ul className="list-reset margin-bottom-s credit-report-dates">
                                                    <li>
                                                        <strong>Updated: </strong>
                                                        {getFormattedDate(i18nService.get("Country"), props.data["update-date"])}
                                                    </li>
                                                    {!props.isOneTimeCreditServiceActive && (
                                                        <li>
                                                            <strong>Next Update Available: </strong>
                                                            {getFormattedDate(i18nService.get("Country"), props.data["next-update-date"])}
                                                        </li>
                                                    )}
                                                </ul>
                                            ) : (
                                                <React.Fragment>
                                                    <ul className="list-reset margin-bottom-s credit-report-dates">
                                                        <li>
                                                            <strong>Updated:</strong>
                                                            {getFormattedDate(i18nService.get("Country"), props.data["update-date"])}
                                                        </li>
                                                    </ul>
                                                </React.Fragment>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    ""
                                )}
                            </div>
                            <div className="box-actions"></div>
                        </div>
                        <div className="box-content">
                            <div className="table-container scroll">
                                <table className="m-table-generic">
                                    <thead>
                                        <tr>
                                            <th scope="col">
                                                <span className="sr-only">Item</span>
                                            </th>
                                            {col.map((summary: ISummaryItems) => (
                                                <th key={summary["credit-bureau"] + "names"} scope="col">
                                                    {summary["credit-bureau"] === "Merge" ? "Total" : summary["credit-bureau"]}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <th scope="row" className="table-label">
                                                Total Accounts
                                            </th>
                                            {col.map((summary: ISummaryItems) => (
                                                <td key={summary["credit-bureau"] + "totalAccounts"}>{summary["total-accounts"]}</td>
                                            ))}
                                        </tr>
                                        <tr>
                                            <th scope="row" className="table-label">
                                                Open Accounts
                                            </th>
                                            {col.map((summary: ISummaryItems) => (
                                                <td key={summary["credit-bureau"] + "openAccounts"}>{summary["open-accounts"]}</td>
                                            ))}
                                        </tr>
                                        <tr>
                                            <th scope="row" className="table-label">
                                                Closed Accounts
                                            </th>
                                            {col.map((summary: ISummaryItems) => (
                                                <td key={summary["credit-bureau"] + "closedAccounts"}>{summary["closed-accounts"]}</td>
                                            ))}
                                        </tr>
                                        <tr>
                                            <th scope="row" className="table-label">
                                                Total Balances
                                            </th>
                                            {col.map((summary: ISummaryItems) => (
                                                <td key={summary["credit-bureau"] + "totalBalances"}>{formatCurrency(summary["total-balances"])}</td>
                                            ))}
                                        </tr>
                                        <tr>
                                            <th scope="row" className="table-label">
                                                Total Monthly Payments
                                            </th>
                                            {col.map((summary: ISummaryItems) => (
                                                <td key={summary["credit-bureau"] + "totalMonthlyPaymnents"}>
                                                    {formatCurrency(summary["total-monthly-payments"])}
                                                </td>
                                            ))}
                                        </tr>
                                        <tr>
                                            <th scope="row" className="table-label">
                                                Delinquent Accounts
                                            </th>
                                            {col.map((summary: ISummaryItems) => (
                                                <td key={summary["credit-bureau"] + "deliquentAccounts"}>{summary["delinquent-accounts"]}</td>
                                            ))}
                                        </tr>
                                        <tr>
                                            <th scope="row" className="table-label">
                                                Inquiries in the Last 2 Years
                                            </th>
                                            {col.map((summary: ISummaryItems) => (
                                                <td key={summary["credit-bureau"] + "inquiriesLast2Years"}>{summary["inquiries-last-2-years"]}</td>
                                            ))}
                                        </tr>
                                        <tr>
                                            <th scope="row" className="table-label">
                                                Number of Public Records
                                            </th>
                                            {col.map((summary: ISummaryItems) => (
                                                <td key={summary["credit-bureau"] + "numberOfPublicRecords"}>{summary["number-public-records"]}</td>
                                            ))}
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </React.Fragment>
                ) : (
                    ""
                )}
            </div>
        </React.Fragment>
    );
};

export default PrintCreditSummary;
